import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import config from "../../config";
import patrimonioService from "../../services/patrimonioService";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import MovimentarPatrimonioModal from "./MovimentarPatrimonioModal";
import usePermission from "../../hooks/usePermission";
import { maskCurrency, formatCurrencyFromDb } from "../../utils/masks";
import { formatDateWithoutHour } from "../../utils/date";
import { TIPOS_PATRIMONIO, SITUACOES_PATRIMONIO, getLocalizacaoLabel, SituacaoBadge, getSituacaoInfo, isBaixado } from "./patrimonioConstants";
import CamposEspecificosTipo, { CAMPOS_ESPECIFICOS_INICIAIS, appendCamposEspecificos } from "./CamposEspecificosTipo";
import DarBaixaModal from "./DarBaixaModal";

// Converte valor mascarado ("R$ 1.234,56") em número ou string vazia
const parseCurrency = (value) => {
    if (!value) return "";
    const parsed = parseFloat(String(value).replace(/[^\d,]/g, "").replace(",", "."));
    return isNaN(parsed) ? "" : parsed;
};

// Converte ISO em valor de input date (YYYY-MM-DD)
const toInputDate = (value) => (value ? String(value).split("T")[0] : "");

const ABAS = [
    { id: "dados", label: "Dados", icon: "📋" },
    { id: "movimentacoes", label: "Movimentações", icon: "🚚" },
    { id: "manutencoes", label: "Manutenções", icon: "🔧" },
    { id: "anexos", label: "Anexos", icon: "📎" },
];

const EditarPatrimonio = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const podeEditar = usePermission("EDITAR_PATRIMONIO");
    const podeMovimentar = usePermission("MOVIMENTAR_PATRIMONIO");
    const podeBaixar = usePermission("BAIXAR_PATRIMONIO");

    const [abaAtiva, setAbaAtiva] = useState("dados");
    const [patrimonio, setPatrimonio] = useState(null);
    const [locais, setLocais] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [obras, setObras] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);

    const [formData, setFormData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [showMovimentarModal, setShowMovimentarModal] = useState(false);
    const [showBaixaModal, setShowBaixaModal] = useState(false);
    const [showReverterConfirm, setShowReverterConfirm] = useState(false);

    // Manutenções
    const [manutencaoForm, setManutencaoForm] = useState({ data: "", descricao: "", valor: "", alterarSituacao: false });
    const [manutencaoEditandoId, setManutencaoEditandoId] = useState(null);
    const [manutencaoParaExcluir, setManutencaoParaExcluir] = useState(null);

    // Anexos
    const [novosAnexos, setNovosAnexos] = useState([]);
    const [anexoParaExcluir, setAnexoParaExcluir] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const fetchPatrimonio = useCallback(async () => {
        try {
            const data = await patrimonioService.buscarPorId(id);
            setPatrimonio(data);
            setFormData({
                codigo: data.codigo || "",
                nome: data.nome || "",
                descricao: data.descricao || "",
                tipo: data.tipo || "EQUIPAMENTO",
                categoriaId: data.categoriaId || "",
                situacao: data.situacao || "ATIVO",
                numeroSerie: data.numeroSerie || "",
                marca: data.marca || "",
                modelo: data.modelo || "",
                ...Object.fromEntries(
                    Object.keys(CAMPOS_ESPECIFICOS_INICIAIS).map((campo) => [campo, data[campo] ?? ""])
                ),
                validadeLicenca: toInputDate(data.validadeLicenca),
                valorAquisicao: data.valorAquisicao != null ? formatCurrencyFromDb(data.valorAquisicao) : "",
                dataAquisicao: toInputDate(data.dataAquisicao),
                fornecedorId: data.fornecedorId || "",
                numeroNotaFiscal: data.numeroNotaFiscal || "",
                garantiaAte: toInputDate(data.garantiaAte),
                vidaUtilMeses: data.vidaUtilMeses ?? "",
                valorResidual: data.valorResidual != null && data.valorResidual !== 0 ? formatCurrencyFromDb(data.valorResidual) : "",
                observacoes: data.observacoes || "",
            });
        } catch (error) {
            console.error("Erro ao carregar patrimônio:", error);
            setAlertMessage("Erro ao carregar o patrimônio.");
        }
    }, [id]);

    useEffect(() => {
        const fetchAuxiliares = async () => {
            try {
                const [locaisRes, categoriasRes, obrasRes, fornecedoresRes] = await Promise.all([
                    patrimonioService.listarLocais(),
                    patrimonioService.listarCategorias(),
                    api.get("/obras/listar/todas"),
                    api.get("/fornecedores/todos"),
                ]);
                setLocais(locaisRes || []);
                setCategorias(categoriasRes || []);
                setObras(obrasRes.data || []);
                setFornecedores(fornecedoresRes.data || []);
            } catch (error) {
                console.error("Erro ao carregar dados auxiliares:", error);
            }
        };
        fetchAuxiliares();
        fetchPatrimonio();
    }, [fetchPatrimonio]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
            // Trocar o tipo invalida a categoria selecionada
            ...(name === "tipo" ? { categoriaId: "" } : {}),
        }));
    };

    const handleCurrencyChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: maskCurrency(value) }));
    };

    const handleSalvarDados = async (e) => {
        e.preventDefault();

        if (!formData.nome.trim()) {
            setAlertMessage("Informe o nome do bem.");
            return;
        }

        const data = new FormData();
        data.append("codigo", formData.codigo);
        data.append("nome", formData.nome);
        data.append("descricao", formData.descricao);
        data.append("tipo", formData.tipo);
        data.append("categoriaId", formData.categoriaId);
        data.append("situacao", formData.situacao);
        data.append("numeroSerie", formData.numeroSerie);
        data.append("marca", formData.marca);
        data.append("modelo", formData.modelo);
        appendCamposEspecificos(data, formData);
        data.append("valorAquisicao", parseCurrency(formData.valorAquisicao));
        data.append("dataAquisicao", formData.dataAquisicao);
        data.append("fornecedorId", formData.fornecedorId);
        data.append("numeroNotaFiscal", formData.numeroNotaFiscal);
        data.append("garantiaAte", formData.garantiaAte);
        data.append("vidaUtilMeses", formData.vidaUtilMeses);
        data.append("valorResidual", parseCurrency(formData.valorResidual));
        data.append("observacoes", formData.observacoes);

        setIsSaving(true);
        try {
            await patrimonioService.atualizar(id, data);
            setSuccessMessage("Patrimônio atualizado com sucesso!");
            fetchPatrimonio();
        } catch (error) {
            console.error("Erro ao atualizar patrimônio:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao atualizar patrimônio.");
        } finally {
            setIsSaving(false);
        }
    };

    // ------------------------------------------------------------------
    // Manutenções
    // ------------------------------------------------------------------

    const limparManutencaoForm = () => {
        setManutencaoForm({ data: "", descricao: "", valor: "", alterarSituacao: false });
        setManutencaoEditandoId(null);
    };

    const handleEditarManutencao = (manutencao) => {
        setManutencaoEditandoId(manutencao.id);
        setManutencaoForm({
            data: toInputDate(manutencao.data),
            descricao: manutencao.descricao || "",
            valor: manutencao.valor != null ? formatCurrencyFromDb(manutencao.valor) : "",
            alterarSituacao: false,
        });
    };

    const handleSalvarManutencao = async (e) => {
        e.preventDefault();
        if (!manutencaoForm.data || !manutencaoForm.descricao.trim()) {
            setAlertMessage("Informe a data e a descrição da manutenção.");
            return;
        }

        const dados = {
            data: manutencaoForm.data,
            descricao: manutencaoForm.descricao,
            valor: parseCurrency(manutencaoForm.valor),
        };

        try {
            if (manutencaoEditandoId) {
                await patrimonioService.atualizarManutencao(id, manutencaoEditandoId, dados);
            } else {
                await patrimonioService.criarManutencao(id, { ...dados, alterarSituacao: manutencaoForm.alterarSituacao });
            }
            setSuccessMessage("Manutenção salva com sucesso!");
            limparManutencaoForm();
            fetchPatrimonio();
        } catch (error) {
            console.error("Erro ao salvar manutenção:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao salvar manutenção.");
        }
    };

    const handleConfirmarExclusaoManutencao = async () => {
        const manutencao = manutencaoParaExcluir;
        setManutencaoParaExcluir(null);
        try {
            await patrimonioService.excluirManutencao(id, manutencao.id);
            fetchPatrimonio();
        } catch (error) {
            console.error("Erro ao excluir manutenção:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao excluir manutenção.");
        }
    };

    // ------------------------------------------------------------------
    // Anexos
    // ------------------------------------------------------------------

    const handleUploadAnexos = async () => {
        if (novosAnexos.length === 0) {
            setAlertMessage("Selecione ao menos um arquivo.");
            return;
        }

        const data = new FormData();
        novosAnexos.forEach((arquivo, index) => {
            data.append(`anexos[${index}]`, arquivo);
        });

        setIsUploading(true);
        try {
            await patrimonioService.uploadAnexos(id, data);
            setSuccessMessage("Anexos enviados com sucesso!");
            setNovosAnexos([]);
            fetchPatrimonio();
        } catch (error) {
            console.error("Erro ao enviar anexos:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao enviar anexos.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleConfirmarExclusaoAnexo = async () => {
        const anexo = anexoParaExcluir;
        setAnexoParaExcluir(null);
        try {
            await patrimonioService.excluirAnexo(id, anexo.id);
            fetchPatrimonio();
        } catch (error) {
            console.error("Erro ao excluir anexo:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao excluir anexo.");
        }
    };

    const isImagem = (arquivo) => /\.(png|jpe?g|gif|webp|bmp)$/i.test(arquivo || "");

    const inputClass = "px-3 py-2 border border-slate-300 rounded-md text-sm w-full";
    const labelClass = "text-sm text-slate-700 font-medium";

    if (!patrimonio || !formData) {
        return (
            <Template>
                <div className="w-full px-4 py-6 text-slate-500">Carregando...</div>
            </Template>
        );
    }

    const depreciacao = patrimonio.depreciacao || {};
    const baixado = isBaixado(patrimonio.situacao);

    const handleReverterBaixa = async () => {
        setShowReverterConfirm(false);
        try {
            await patrimonioService.reverterBaixa(id);
            setSuccessMessage("Baixa revertida com sucesso! O bem voltou para a situação Ativo.");
            fetchPatrimonio();
        } catch (error) {
            console.error("Erro ao reverter baixa:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao reverter a baixa.");
        }
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-6">
                {/* Cabeçalho */}
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl text-stone-800 font-medium">
                            {patrimonio.codigo} - {patrimonio.nome}
                        </h1>
                        <SituacaoBadge situacao={patrimonio.situacao} />
                    </div>
                    <div className="flex items-center gap-2">
                        {!baixado && podeMovimentar && (
                            <button
                                type="button"
                                className="px-3 py-2 sm:px-4 bg-stone-600 text-white rounded-md flex items-center gap-2"
                                onClick={() => setShowMovimentarModal(true)}
                            >
                                <img className="w-4 h-4" src="/images/icon/truck.png" alt="Movimentar" />
                                <span className="text-sm font-medium">Movimentar</span>
                            </button>
                        )}
                        {!baixado && podeBaixar && (
                            <button
                                type="button"
                                className="px-3 py-2 sm:px-4 bg-red-700 text-white rounded-md flex items-center gap-2"
                                onClick={() => setShowBaixaModal(true)}
                            >
                                <img className="w-4 h-4" src="/images/icon/x-circle.png" alt="Dar Baixa" />
                                <span className="text-sm font-medium">Dar Baixa</span>
                            </button>
                        )}
                        {baixado && podeBaixar && (
                            <button
                                type="button"
                                className="px-3 py-2 sm:px-4 bg-white rounded-md border border-stone-500 flex items-center gap-2"
                                onClick={() => setShowReverterConfirm(true)}
                            >
                                <span className="text-stone-600 text-sm font-medium">Reverter Baixa</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Abas */}
                <div className="bg-white rounded-t-xl shadow-sm">
                    <div className="flex border-b-2 border-gray-200 px-2 sm:px-6 overflow-auto">
                        {ABAS.map((aba) => (
                            <button
                                key={aba.id}
                                onClick={() => setAbaAtiva(aba.id)}
                                style={{
                                    padding: "14px 20px",
                                    border: "none",
                                    backgroundColor: "transparent",
                                    borderBottom: abaAtiva === aba.id ? "3px solid #57534E" : "3px solid transparent",
                                    color: abaAtiva === aba.id ? "#57534E" : "#6B7280",
                                    fontWeight: abaAtiva === aba.id ? "600" : "500",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <span style={{ fontSize: "16px" }}>{aba.icon}</span>
                                <span>{aba.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* === ABA DADOS === */}
                {abaAtiva === "dados" && (
                    <div className="flex flex-col lg:flex-row gap-6">
                        <form onSubmit={handleSalvarDados} className="flex-1 flex flex-col gap-6">
                            {/* Dados gerais */}
                            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                                <div className="text-stone-600 text-base font-medium">Dados Gerais</div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Código</label>
                                        <input type="text" name="codigo" value={formData.codigo} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                    <div className="flex flex-col gap-1.5 md:col-span-2">
                                        <label className={labelClass}>Nome do bem *</label>
                                        <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} className={inputClass} required disabled={!podeEditar} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Tipo *</label>
                                        <select name="tipo" value={formData.tipo} onChange={handleInputChange} className={inputClass} disabled={!podeEditar}>
                                            {TIPOS_PATRIMONIO.map((t) => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Categoria</label>
                                        <select name="categoriaId" value={formData.categoriaId} onChange={handleInputChange} className={inputClass} disabled={!podeEditar}>
                                            <option value="">Selecionar</option>
                                            {categorias
                                                .filter((c) => c.tipo === formData.tipo)
                                                .map((c) => (
                                                    <option key={c.id} value={c.id}>{c.nome}</option>
                                                ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Situação</label>
                                        {baixado ? (
                                            <input
                                                type="text"
                                                value={getSituacaoInfo(patrimonio.situacao).label}
                                                className={`${inputClass} bg-stone-100`}
                                                disabled
                                                title='Bem baixado — use "Reverter Baixa" para reativar'
                                            />
                                        ) : (
                                            <select name="situacao" value={formData.situacao} onChange={handleInputChange} className={inputClass} disabled={!podeEditar}>
                                                {SITUACOES_PATRIMONIO.filter((s) => ["ATIVO", "EM_MANUTENCAO"].includes(s.value)).map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Nº de Série</label>
                                        <input type="text" name="numeroSerie" value={formData.numeroSerie} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Marca</label>
                                        <input type="text" name="marca" value={formData.marca} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Modelo</label>
                                        <input type="text" name="modelo" value={formData.modelo} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className={labelClass}>Descrição</label>
                                    <textarea name="descricao" rows={2} value={formData.descricao} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                </div>

                            </div>

                            {/* Dados da baixa (somente leitura, quando baixado) */}
                            {baixado && (
                                <div className="bg-red-50 p-4 rounded-md border border-red-200 flex flex-col gap-3">
                                    <div className="flex items-center justify-between">
                                        <div className="text-red-800 text-base font-medium">Bem Baixado</div>
                                        <SituacaoBadge situacao={patrimonio.situacao} />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-slate-500">Data da baixa: </span>
                                            <span className="text-slate-700 font-medium">
                                                {patrimonio.dataBaixa ? formatDateWithoutHour(patrimonio.dataBaixa) : "-"}
                                            </span>
                                        </div>
                                        {patrimonio.baixaDestinatarioNome && (
                                            <div>
                                                <span className="text-slate-500">
                                                    {patrimonio.situacao === "DOADO" ? "Donatário" : "Destinatário"}
                                                    {patrimonio.baixaDestinatarioTipo ? ` (${patrimonio.baixaDestinatarioTipo})` : ""}:{" "}
                                                </span>
                                                <span className="text-slate-700 font-medium">
                                                    {patrimonio.baixaDestinatarioNome}
                                                    {patrimonio.baixaDestinatarioDocumento ? ` — ${patrimonio.baixaDestinatarioDocumento}` : ""}
                                                </span>
                                            </div>
                                        )}
                                        {patrimonio.valorVenda != null && (
                                            <div>
                                                <span className="text-slate-500">Valor da venda: </span>
                                                <span className="text-slate-700 font-medium">{formatCurrencyFromDb(patrimonio.valorVenda)}</span>
                                            </div>
                                        )}
                                        <div className="md:col-span-2">
                                            <span className="text-slate-500">Motivo: </span>
                                            <span className="text-slate-700 font-medium">{patrimonio.motivoBaixa || "-"}</span>
                                        </div>
                                    </div>
                                    <div className="text-slate-400 text-xs">
                                        Bens baixados não podem ser movimentados. Use "Reverter Baixa" para reativar.
                                    </div>
                                </div>
                            )}

                            {/* Campos específicos do tipo (Veículo / Imóvel) */}
                            <CamposEspecificosTipo tipo={formData.tipo} formData={formData} setFormData={setFormData} disabled={!podeEditar} />

                            {/* Localização atual (somente leitura) */}
                            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-stone-600 text-base font-medium">Localização Atual</div>
                                    {!baixado && podeMovimentar && (
                                        <button
                                            type="button"
                                            className="px-3 py-1.5 bg-white rounded-md border border-stone-500 text-stone-600 text-sm font-medium"
                                            onClick={() => setShowMovimentarModal(true)}
                                        >
                                            Movimentar
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-slate-500">Localização: </span>
                                        <span className="text-slate-700 font-medium">{getLocalizacaoLabel(patrimonio)}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Responsável: </span>
                                        <span className="text-slate-700 font-medium">{patrimonio.responsavel || "-"}</span>
                                    </div>
                                </div>
                                <div className="text-slate-400 text-xs">
                                    A localização e o responsável só mudam através de uma movimentação, para manter o histórico.
                                </div>
                            </div>

                            {/* Aquisição e Garantia */}
                            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                                <div className="text-stone-600 text-base font-medium">Aquisição, Garantia e Depreciação</div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Valor de Aquisição (R$)</label>
                                        <input type="text" name="valorAquisicao" value={formData.valorAquisicao} onChange={handleCurrencyChange} className={inputClass} placeholder="R$ 0,00" disabled={!podeEditar} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Data de Aquisição</label>
                                        <input type="date" name="dataAquisicao" value={formData.dataAquisicao} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Garantia até</label>
                                        <input type="date" name="garantiaAte" value={formData.garantiaAte} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Fornecedor</label>
                                        <select name="fornecedorId" value={formData.fornecedorId} onChange={handleInputChange} className={inputClass} disabled={!podeEditar}>
                                            <option value="">Selecionar</option>
                                            {fornecedores.map((f) => (
                                                <option key={f.id} value={f.id}>{f.nome || f.nomeFornecedor}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Nº da Nota Fiscal</label>
                                        <input type="text" name="numeroNotaFiscal" value={formData.numeroNotaFiscal} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Vida útil (meses)</label>
                                        <input type="number" min="0" name="vidaUtilMeses" value={formData.vidaUtilMeses} onChange={handleInputChange} className={inputClass} placeholder="Ex: 60" disabled={!podeEditar} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Valor Residual (R$)</label>
                                        <input type="text" name="valorResidual" value={formData.valorResidual} onChange={handleCurrencyChange} className={inputClass} placeholder="R$ 0,00" disabled={!podeEditar} />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className={labelClass}>Observações</label>
                                    <textarea name="observacoes" rows={3} value={formData.observacoes} onChange={handleInputChange} className={inputClass} disabled={!podeEditar} />
                                </div>
                            </div>

                            {/* Botões */}
                            <div className="flex items-center w-full gap-4">
                                <div className="flex gap-4 ml-auto">
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                                        onClick={() => navigate("/patrimonios")}
                                    >
                                        Voltar
                                    </button>
                                    {podeEditar && (
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-50"
                                            disabled={isSaving}
                                        >
                                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                                            <span>{isSaving ? "Salvando..." : "Salvar"}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>

                        {/* Card de depreciação */}
                        <div className="w-full lg:w-80 flex flex-col gap-4">
                            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-3">
                                <div className="text-stone-600 text-base font-medium">Resumo Financeiro</div>
                                <div className="flex flex-col gap-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Valor de aquisição</span>
                                        <span className="text-slate-700 font-medium">{formatCurrencyFromDb(patrimonio.valorAquisicao) || "-"}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Depreciação acumulada</span>
                                        <span className="text-slate-700 font-medium">{formatCurrencyFromDb(depreciacao.depreciacaoAcumulada) || "R$ 0,00"}</span>
                                    </div>
                                    <div className="flex justify-between border-t border-slate-100 pt-2">
                                        <span className="text-slate-500 font-medium">Valor atual</span>
                                        <span className="text-stone-800 font-bold">{formatCurrencyFromDb(patrimonio.valorAtual) || "-"}</span>
                                    </div>
                                    {depreciacao.depreciavel != null && (
                                        <>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">% depreciado</span>
                                                <span className="text-slate-700 font-medium">{(depreciacao.percentualDepreciado || 0).toFixed(1)}%</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Meses decorridos</span>
                                                <span className="text-slate-700 font-medium">
                                                    {depreciacao.mesesEfetivos} / {patrimonio.vidaUtilMeses}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {patrimonio.garantiaAte && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Garantia até</span>
                                            <span className={`font-medium ${new Date(patrimonio.garantiaAte) < new Date() ? "text-red-600" : "text-green-700"}`}>
                                                {formatDateWithoutHour(patrimonio.garantiaAte)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* === ABA MOVIMENTAÇÕES === */}
                {abaAtiva === "movimentacoes" && (
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                            <div className="text-stone-600 text-base font-medium">Histórico de Movimentações</div>
                            {!baixado && podeMovimentar && (
                                <button
                                    type="button"
                                    className="px-3 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                                    onClick={() => setShowMovimentarModal(true)}
                                >
                                    Nova Movimentação
                                </button>
                            )}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-stone-100">
                                        <th className="px-4 py-2 border border-slate-200 text-sm">Data</th>
                                        <th className="px-4 py-2 border border-slate-200 text-sm">De (Localização)</th>
                                        <th className="px-4 py-2 border border-slate-200 text-sm">Para (Localização)</th>
                                        <th className="px-4 py-2 border border-slate-200 text-sm">De (Responsável)</th>
                                        <th className="px-4 py-2 border border-slate-200 text-sm">Para (Responsável)</th>
                                        <th className="px-4 py-2 border border-slate-200 text-sm">Observação</th>
                                        <th className="px-4 py-2 border border-slate-200 text-sm">Registrado por</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(patrimonio.movimentacoes || []).length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-4 text-center text-slate-500 text-sm">
                                                Nenhuma movimentação registrada.
                                            </td>
                                        </tr>
                                    ) : (
                                        patrimonio.movimentacoes.map((mov) => {
                                            const deLoc = mov.deObra
                                                ? `Obra: ${mov.deObra.codigoProjeto} - ${mov.deObra.nomeProjeto}`
                                                : mov.deLocal?.nome || "-";
                                            const paraLoc = mov.paraObra
                                                ? `Obra: ${mov.paraObra.codigoProjeto} - ${mov.paraObra.nomeProjeto}`
                                                : mov.paraLocal?.nome || "-";
                                            return (
                                                <tr key={mov.id} className="border-b">
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{formatDateWithoutHour(mov.dataMovimentacao)}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{deLoc}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm font-medium">{paraLoc}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{mov.deResponsavel || "-"}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm font-medium">{mov.paraResponsavel || "-"}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{mov.observacao || "-"}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{mov.criadoPor?.nome || "-"}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* === ABA MANUTENÇÕES === */}
                {abaAtiva === "manutencoes" && (
                    <div className="flex flex-col gap-6">
                        {podeEditar && (
                            <form onSubmit={handleSalvarManutencao} className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                                <div className="text-stone-600 text-base font-medium">
                                    {manutencaoEditandoId ? "Editar Manutenção" : "Registrar Manutenção"}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Data *</label>
                                        <input
                                            type="date"
                                            value={manutencaoForm.data}
                                            onChange={(e) => setManutencaoForm((prev) => ({ ...prev, data: e.target.value }))}
                                            className={inputClass}
                                            required
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className={labelClass}>Valor (R$)</label>
                                        <input
                                            type="text"
                                            value={manutencaoForm.valor}
                                            onChange={(e) => setManutencaoForm((prev) => ({ ...prev, valor: maskCurrency(e.target.value) }))}
                                            className={inputClass}
                                            placeholder="R$ 0,00"
                                        />
                                    </div>
                                    {!manutencaoEditandoId && (
                                        <div className="flex items-end pb-2">
                                            <label className="inline-flex items-center gap-2 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={manutencaoForm.alterarSituacao}
                                                    onChange={(e) => setManutencaoForm((prev) => ({ ...prev, alterarSituacao: e.target.checked }))}
                                                />
                                                <span>Marcar bem como "Em Manutenção"</span>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className={labelClass}>Descrição *</label>
                                    <textarea
                                        rows={2}
                                        value={manutencaoForm.descricao}
                                        onChange={(e) => setManutencaoForm((prev) => ({ ...prev, descricao: e.target.value }))}
                                        className={inputClass}
                                        placeholder="Ex: troca de óleo, reparo na fonte, revisão geral..."
                                        required
                                    />
                                </div>

                                <div className="flex items-center gap-4">
                                    {manutencaoEditandoId && (
                                        <button type="button" onClick={limparManutencaoForm} className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md">
                                            Cancelar
                                        </button>
                                    )}
                                    <button type="submit" className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md">
                                        {manutencaoEditandoId ? "Salvar Alterações" : "Adicionar Manutenção"}
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                            <div className="text-stone-600 text-base font-medium">Manutenções Registradas</div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-stone-100">
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Data</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Descrição</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Valor</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Registrado por</th>
                                            {podeEditar && <th className="px-4 py-2 border border-slate-200 text-sm">Ações</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(patrimonio.manutencoes || []).length === 0 ? (
                                            <tr>
                                                <td colSpan={podeEditar ? 5 : 4} className="px-4 py-4 text-center text-slate-500 text-sm">
                                                    Nenhuma manutenção registrada.
                                                </td>
                                            </tr>
                                        ) : (
                                            patrimonio.manutencoes.map((manutencao) => (
                                                <tr key={manutencao.id} className="border-b">
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{formatDateWithoutHour(manutencao.data)}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{manutencao.descricao}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{formatCurrencyFromDb(manutencao.valor) || "-"}</td>
                                                    <td className="px-4 py-2 border border-slate-200 text-sm">{manutencao.criadoPor?.nome || "-"}</td>
                                                    {podeEditar && (
                                                        <td className="px-4 py-2 border border-slate-200 text-sm">
                                                            <button type="button" onClick={() => handleEditarManutencao(manutencao)} className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2">
                                                                Editar
                                                            </button>
                                                            <button type="button" onClick={() => setManutencaoParaExcluir(manutencao)} className="px-2 py-1 bg-red-600 text-white rounded-md text-sm">
                                                                Excluir
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* === ABA ANEXOS === */}
                {abaAtiva === "anexos" && (
                    <div className="flex flex-col gap-6">
                        {podeEditar && (
                            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                                <div className="text-stone-600 text-base font-medium">Enviar Fotos / Documentos</div>
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*,.pdf"
                                        onChange={(e) => setNovosAnexos(Array.from(e.target.files || []))}
                                        className="text-sm"
                                    />
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md disabled:opacity-50"
                                        onClick={handleUploadAnexos}
                                        disabled={isUploading || novosAnexos.length === 0}
                                    >
                                        {isUploading ? "Enviando..." : "Enviar"}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                            <div className="text-stone-600 text-base font-medium">Arquivos do Bem</div>
                            {(patrimonio.anexos || []).length === 0 ? (
                                <div className="text-slate-500 text-sm py-4 text-center">Nenhum anexo enviado.</div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {patrimonio.anexos.map((anexo) => {
                                        const url = config.urlArquivo(anexo.arquivo);
                                        const nomeArquivo = (anexo.arquivo || "").split("/").pop()?.replace(/^\d+_/, "") || "arquivo";
                                        return (
                                            <div key={anexo.id} className="border border-slate-200 rounded-md overflow-hidden flex flex-col">
                                                <a href={url} target="_blank" rel="noreferrer" className="block">
                                                    {isImagem(anexo.arquivo) ? (
                                                        <img src={url} alt={nomeArquivo} className="w-full h-32 object-cover" />
                                                    ) : (
                                                        <div className="w-full h-32 flex items-center justify-center bg-stone-50">
                                                            <img src="/images/icon/pdf.png" alt="Documento" className="w-12 h-12" />
                                                        </div>
                                                    )}
                                                </a>
                                                <div className="px-2 py-2 flex items-center justify-between gap-2">
                                                    <div className="text-xs text-slate-600 truncate" title={nomeArquivo}>{nomeArquivo}</div>
                                                    {podeEditar && (
                                                        <img
                                                            src="/images/icon/trash.png"
                                                            alt="Excluir"
                                                            className="w-4 h-4 cursor-pointer flex-shrink-0"
                                                            onClick={() => setAnexoParaExcluir(anexo)}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Modais */}
            <MovimentarPatrimonioModal
                isVisible={showMovimentarModal}
                patrimonio={patrimonio}
                locais={locais}
                obras={obras}
                onClose={() => setShowMovimentarModal(false)}
                onSuccess={() => {
                    setShowMovimentarModal(false);
                    setSuccessMessage("Movimentação registrada com sucesso!");
                    fetchPatrimonio();
                }}
            />

            <DarBaixaModal
                isVisible={showBaixaModal}
                patrimonio={patrimonio}
                onClose={() => setShowBaixaModal(false)}
                onSuccess={() => {
                    setShowBaixaModal(false);
                    setSuccessMessage("Baixa registrada com sucesso!");
                    fetchPatrimonio();
                }}
            />

            <ConfirmationModal
                isVisible={showReverterConfirm}
                onConfirm={handleReverterBaixa}
                onCancel={() => setShowReverterConfirm(false)}
                message="Deseja reverter a baixa deste bem? Ele voltará para a situação Ativo."
                textButtonConfirm="Sim, reverter"
            />

            <ConfirmationModal
                isVisible={!!manutencaoParaExcluir}
                onConfirm={handleConfirmarExclusaoManutencao}
                onCancel={() => setManutencaoParaExcluir(null)}
                message="Deseja excluir esta manutenção?"
                textButtonConfirm="Sim"
            />

            <ConfirmationModal
                isVisible={!!anexoParaExcluir}
                onConfirm={handleConfirmarExclusaoAnexo}
                onCancel={() => setAnexoParaExcluir(null)}
                message="Deseja excluir este anexo?"
                textButtonConfirm="Sim"
            />

            <SuccessModal
                isVisible={!!successMessage}
                message={successMessage}
                onClose={() => setSuccessMessage("")}
            />

            <AlertDialog
                isVisible={!!alertMessage}
                message={alertMessage}
                onClose={() => setAlertMessage("")}
            />
        </Template>
    );
};

export default EditarPatrimonio;
